import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Check, X, ArrowRight, Zap, Crown, Sparkles, Menu } from 'lucide-react';
import {
  fetchPublicPlans,
  findPlan,
  formatAccountsLabel,
  formatApiKeysLabel,
  formatPlanPrice,
  formatRetentionLabel,
} from '../../lib/planCatalog';

const FEATURE_LABELS = {
  monteCarlo: 'Monte Carlo simulation',
  pdfReports: 'PDF reports',
  brokerComparison: 'Broker comparison per EA',
  prioritySupport: 'Priority support',
};

const PLAN_VISUALS = {
  free: { icon: Zap, gradient: 'linear-gradient(135deg, #10b981, #14b8a6)', glow: 'rgba(16,185,129,0.3)', cta: 'Start Free' },
  pro: { icon: Crown, gradient: 'linear-gradient(135deg, #a855f7, #ec4899)', glow: 'rgba(168,85,247,0.4)', cta: 'Go Pro', popular: true },
  elite: { icon: Crown, gradient: 'linear-gradient(135deg, #f59e0b, #ef4444)', glow: 'rgba(239,68,68,0.4)', cta: 'Get Elite' },
};

export default function PricingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    fetchPublicPlans()
      .then(setPlans)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const freePlan = findPlan(plans, 'free');
  const proPlan = findPlan(plans, 'pro');

  const buildFeatures = (plan) => {
    const items = [
      { text: formatAccountsLabel(plan), included: true },
      { text: 'Real-time 60s sync', included: true },
      { text: 'Per-EA performance breakdown', included: true },
      { text: 'Equity curves & drawdown tracking', included: true },
      { text: 'Session & time analysis', included: true },
      { text: 'Monthly heatmap', included: true },
      { text: formatApiKeysLabel(plan), included: true },
      { text: 'CSV export', included: true },
      { text: formatRetentionLabel(plan), included: true },
    ];

    Object.keys(FEATURE_LABELS).forEach((key) => {
      items.push({ text: FEATURE_LABELS[key], included: !!plan.features?.[key] });
    });

    return items;
  };

  const faqs = [
    {
      q: 'Is there really a 7-day free trial?',
      a: `Yes! When you sign up, you get 7 days of full PRO access automatically - no credit card required. Try Monte Carlo, PDF reports, broker comparison, and all premium features. After 7 days, you can either upgrade to PRO (${formatPlanPrice(proPlan) || 'current paid pricing'}) or stay on the free plan with ${formatAccountsLabel(freePlan).toLowerCase()} and basic features.`,
    },
    {
      q: 'Is the free plan really free forever?',
      a: `Yes. After your 7-day PRO trial ends, you can stay on the free plan forever with no credit card required. The free plan includes all core tracking features for ${formatAccountsLabel(freePlan).toLowerCase()}.`,
    },
    { q: 'Can I switch between plans?', a: 'Upgrade or downgrade anytime. If you downgrade, you keep access until the end of your billing period.' },
    { q: 'What happens to my data if I cancel Pro?', a: 'Your data stays. You just lose access to Pro features (Monte Carlo, PDF reports, etc). You can always export everything as CSV.' },
    { q: 'Do you support MT4?', a: 'Yes! Both MT4 and MT5 are fully supported. The EA works identically on both platforms.' },
    { q: 'Is my trading data secure?', a: 'Your data is encrypted in transit and at rest. We never have access to your broker credentials - the EA only sends trade data, not login info.' },
  ];

  return (
    <div className="min-h-screen bg-[#070912] text-gray-100 overflow-x-hidden relative">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-purple-600/8 blur-[140px] animate-float-slow" />
        <div className="absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full bg-pink-500/6 blur-[130px] animate-float" />
        <div className="absolute bottom-0 left-1/3 w-[550px] h-[550px] rounded-full bg-emerald-500/6 blur-[140px] animate-float-slow" />
      </div>

      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-[#070912]/85 backdrop-blur-xl border-b border-white/8' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 bg-gradient-to-br from-emerald-400 via-teal-400 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/30 group-hover:scale-105 transition-transform">
              <BarChart3 size={18} className="text-white" />
            </div>
            <span className="text-lg font-bold text-white font-display">TrackMyFxEA</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/explore" className="text-sm text-gray-400 hover:text-white transition-colors">Explore</Link>
            <Link to="/features" className="text-sm text-gray-400 hover:text-white transition-colors">Features</Link>
            <Link to="/how-it-works" className="text-sm text-gray-400 hover:text-white transition-colors">How It Works</Link>
            <Link to="/pricing" className="text-sm text-emerald-400 font-medium">Pricing</Link>
            <Link to="/ea-store" className="text-sm text-gray-400 hover:text-white transition-colors">EA Store</Link>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <Link to="/login" className="text-sm text-gray-300 hover:text-white transition-colors px-4 py-2">Sign In</Link>
            <Link to="/register" className="text-sm bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-emerald-500/30 transition-all">Get Started</Link>
          </div>
          <button type="button" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-gray-300 hover:text-white p-2" aria-label="Toggle navigation">
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/8 bg-[#070912]/95 backdrop-blur-xl px-4 py-4 space-y-3">
            <Link to="/explore" className="block text-sm text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>Explore</Link>
            <Link to="/features" className="block text-sm text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>Features</Link>
            <Link to="/how-it-works" className="block text-sm text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>How It Works</Link>
            <Link to="/pricing" className="block text-sm text-emerald-400 py-2" onClick={() => setMobileMenuOpen(false)}>Pricing</Link>
            <Link to="/ea-store" className="block text-sm text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>EA Store</Link>
            <hr className="border-white/8" />
            <Link to="/login" className="block text-sm text-gray-300 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>Sign In</Link>
            <Link to="/register" className="block text-sm bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-medium px-5 py-2.5 rounded-xl text-center" onClick={() => setMobileMenuOpen(false)}>Get Started</Link>
          </div>
        )}
      </nav>

      <section className="pt-32 md:pt-40 pb-16 px-4 md:px-6 text-center relative">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500/20 to-emerald-500/20 border border-purple-500/30 rounded-full px-4 py-1.5 mb-7 backdrop-blur-sm animate-fade-in-down">
            <Sparkles size={12} className="text-purple-400" />
            <span className="text-xs text-gray-200 font-medium">7-Day Free PRO Trial - No credit card required</span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-5 font-display animate-fade-in-up">
            Simple, <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-emerald-400 bg-clip-text text-transparent">Transparent</span> Pricing
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto text-base md:text-lg animate-fade-in-up">
            Try all PRO features free for 7 days. Then choose the plan that fits.
          </p>
        </div>
      </section>

      <section className="px-4 md:px-6 pb-20 relative">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {loading ? (
            <div className="md:col-span-2 text-center py-20 text-gray-500">Loading plans...</div>
          ) : !plans.length ? (
            <div className="md:col-span-2 text-center py-20 text-gray-500">No plans available</div>
          ) : (
            plans.map((plan) => {
              const visuals = PLAN_VISUALS[plan.slug] || PLAN_VISUALS.pro;
              const Icon = visuals.icon;
              const features = buildFeatures(plan);
              const displayPrice = formatPlanPrice(plan) || '';
              const [price, periodSuffix = ''] = displayPrice.split('/');
              const period = periodSuffix ? `/${periodSuffix}` : '';
              const yearlyPrice = formatPlanPrice(plan, 'yearly');

              return (
                <div
                  key={plan.slug}
                  className={`group relative rounded-3xl border p-8 md:p-10 transition-all duration-300 hover:-translate-y-1 backdrop-blur-sm ${visuals.popular ? 'border-purple-500/30' : 'border-white/10'}`}
                  style={{
                    background: visuals.popular ? 'linear-gradient(135deg, rgba(168,85,247,0.04), rgba(236,72,153,0.02))' : 'rgba(255,255,255,0.02)',
                    boxShadow: visuals.popular ? '0 0 60px rgba(168,85,247,0.15)' : 'none',
                  }}
                >
                  {visuals.popular && (
                    <div
                      className="absolute -top-4 left-1/2 -translate-x-1/2 text-white text-xs font-bold px-4 py-1.5 rounded-full"
                      style={{
                        background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                        boxShadow: '0 4px 16px rgba(168,85,247,0.4)',
                      }}
                    >
                      MOST POPULAR
                    </div>
                  )}

                  <div className="flex items-center gap-3 mb-6">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{
                        background: visuals.popular ? 'rgba(168,85,247,0.12)' : 'rgba(16,185,129,0.12)',
                        boxShadow: `0 0 20px ${visuals.glow}`,
                      }}
                    >
                      <Icon size={22} className={visuals.popular ? 'text-purple-400' : 'text-emerald-400'} />
                    </div>
                    <h3 className="text-2xl font-bold text-white font-display">{plan.name}</h3>
                  </div>

                  <div className="mb-5">
                    <span className="text-5xl font-bold text-white font-display">{price}</span>
                    <span className="text-gray-500 text-lg ml-2">{period}</span>
                    {plan.priceYearly > 0 && yearlyPrice && (
                      <p className="text-xs text-gray-500 mt-1">{yearlyPrice} (save {Math.round((1 - plan.priceYearly / (plan.priceMonthly * 12)) * 100)}%)</p>
                    )}
                  </div>

                  <Link
                    to="/register"
                    className="block text-center font-bold py-4 rounded-xl mb-10 transition-all hover:shadow-xl text-white"
                    style={{
                      background: visuals.gradient,
                      boxShadow: `0 4px 20px ${visuals.glow}`,
                    }}
                  >
                    {visuals.cta}
                  </Link>

                  <ul className="space-y-3.5">
                    {features.map(({ text, included }, index) => (
                      <li key={index} className="flex items-center gap-3 text-sm">
                        {included ? (
                          <div className="w-5 h-5 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                            <Check size={12} className="text-emerald-400" />
                          </div>
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-gray-800 flex items-center justify-center flex-shrink-0">
                            <X size={12} className="text-gray-700" />
                          </div>
                        )}
                        <span className={included ? 'text-gray-300' : 'text-gray-600'}>{text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })
          )}
        </div>
      </section>

      <section className="px-4 md:px-6 py-20 md:py-24 bg-[#080c14] relative">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12 font-display">
            Frequently Asked <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Questions</span>
          </h2>
          <div className="space-y-5">
            {faqs.map(({ q, a }) => (
              <div
                key={q}
                className="border border-white/8 rounded-2xl p-6 md:p-7 backdrop-blur-sm hover:border-white/15 transition-all"
                style={{ background: 'rgba(255,255,255,0.02)' }}
              >
                <h3 className="text-white font-semibold mb-3 text-base">{q}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-white/8 px-6 py-10 text-center text-xs text-gray-600 bg-[#060810]">
        <p>Copyright 2026 TrackMyFxEA. All rights reserved.</p>
        <p className="mt-2"><a href="mailto:support@trackmyfxea.com" className="hover:text-gray-400 transition-colors">support@trackmyfxea.com</a></p>
<p className="mt-3"><Link to="/privacy" className="mx-2 hover:text-gray-400 transition-colors">Privacy</Link><Link to="/terms" className="mx-2 hover:text-gray-400 transition-colors">Terms</Link><Link to="/risk-disclaimer" className="mx-2 hover:text-gray-400 transition-colors">Risk Disclaimer</Link></p>
      </footer>
    </div>
  );
}
